//@ts-strict-ignore
import React, { CSSProperties, forwardRef, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import ClearInput from '../ClearInput';
import InputPrefix from '../InputPrefix';
import InputSuffix from '../InputSuffix';
import InputBlock from '../InputBlock';
import InputGroup from '../InputGroup';
import InputLabel, { IInputLabelProps } from '../InputLabel';
import Input from '../Input';
import TextArea from '../TextArea';
import InputErrorMessage from '../InputErrorMessage';
import InputInfoMessage from '../InputInfoMessage';
import TriggerPopover from '../TriggerPopover';
import DeprecatedTooltip from '../Tooltip';
import ExpandingTextArea from '../ExpandingTextArea';
import CurrencyInput from '../CurrencyInput';
import MaskInput from './MaskInput';
import getValue from '../utils/getValue';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import Icon from '@mdi/react';
import { mdiContentCopy } from '@mdi/js';
import styled from 'styled-components';
import ICommonFieldProps from '../../../@types/ICommonFieldProps';
import { Maybe } from '../../../graphql/schema/p4types';
import { usePrecision } from '../../../utils/hooks';
import { formatValue } from '../../grid-addons/formatters/FormatValue';
import { p4Tokens } from '../../../styles/themes/default/p4FluentTheme';

export enum BlurTextFieldType {
  Currency = 'currency',
  BaseCurrency = 'baseCurrency',
  TextArea = 'textarea',
  FluentMask = 'fluent_mask',
  ExpandableTextArea = 'expanding_textarea',
  Text = 'text',
  Number = 'number',
  Hide = 'hide',
  Password = 'password',
}

export enum HrefLink {
  MailTo = 'mailto', // for send email link
  Tel = 'tel', // for telephone call number link
  CallTo = 'callto', // for telephone call number link (Skype only)
}

const CopyInput = ({ value }: { value: Maybe<string | number> }) => {
  const CopyIcon = styled(Icon)`
    cursor: pointer;
    padding: 0.3rem;
    display: inline-block;
    position: absolute;
    right: 0.3rem;
    top: 0;
  `;

  return (
    <CopyToClipboard text={value}>
      <CopyIcon path={mdiContentCopy} title="Copy" size={1.1} />
    </CopyToClipboard>
  );
};

const getTextComponent: any = (type) => {
  switch (type) {
    case BlurTextFieldType.TextArea:
      return { TextComponent: TextArea };
    case BlurTextFieldType.ExpandableTextArea:
      return {
        TextComponent: ExpandingTextArea,
        style: {
          borderRadius: 0,
          fontSize: '14px',
          borderWidth: '0px 0px 1px 0px',
          borderStyle: 'solid',
          borderColor: 'lightgray',
        },
      };
    case BlurTextFieldType.Currency:
    case BlurTextFieldType.BaseCurrency:
      return { TextComponent: CurrencyInput };
    case BlurTextFieldType.FluentMask:
      return { TextComponent: MaskInput };
    default:
      return { TextComponent: Input };
  }
};

export type IBlurTextFieldProps = ICommonFieldProps & {
  active?: boolean;
  clearButton?: boolean;
  mandatory?: boolean;
  onChange?: Function; //TODO - Proper typing
  includeLabel?: boolean;
  inputPrefix?: string;
  inputSuffix?: string | object;
  readOnly?: boolean;
  flexBox?: boolean;
  labelCols?: string | number;
  inputCols?: string | number;
  id?: string;
  hint?: string | ReactNode;
  value: Maybe<string | number>;
  validationErrors?: Array<any> | object;
  showWhenNotActive?: boolean;
  label?: Maybe<string>;
  type?: BlurTextFieldType;
  touched?: any;
  hyperlink?: string | boolean;
  customValidationErrors?: any;
  decimals?: number;
  infoMessage?: string;
  spellCheck?: string;
  labelAddon?: any;
  validationHint?: string;
  alignLabel?: any;
  reformatLabel?: any;
  inGrid?: boolean;
  enableOnChange?: boolean;
  onClick?: any;
  style?: any;
  onKeyPress?: any;
  onFocus?: any;
  onBlur?: any;
  readOnlyMessage?: string;
  placeholder?: string;
  valueType?: string;
  autoFocus?: boolean;
  copy?: boolean;
  initialized?: boolean;
  primary?: boolean;
  datatype?: 'numeric'; //Possibly used by 'input' subcomponent.
  className?: string;
  prefix?: string;
  suffix?: string;
  disabled?: boolean;
  textAlign?: 'left' | 'right';
  styles?: {
    input: CSSProperties;
  };
  mask?: string; //BlurTextFieldType.FluentMask only.
  height?: string; //Passed through to input subcomponent (At least for textarea).
  labelProps?: IInputLabelProps; //New approach to handling props for the field label - Prefer this over pass-through props for new code.
  rowData?: any;
  thousandsSeparator?: boolean;
  clearable?: boolean; //Adds a "Clear" button to the top right, only used for BlurTextFieldType.TextArea
};

//TODO - Figure out if we ever pass a ref to this component that potentially needs handling.
// eslint-disable-next-line complexity
const BlurTextField = forwardRef((props: IBlurTextFieldProps, ref: any) => {
  const {
    active,
    type,
    id,
    label,
    clearButton,
    includeLabel,
    inputPrefix,
    inputSuffix,
    flexBox,
    labelCols,
    inputCols,
    readOnly,
    mandatory,
    hint,
    value,
    validationErrors,
    showWhenNotActive,
    touched,
    hyperlink,
    customValidationErrors,
    decimals,
    infoMessage,
    spellCheck,
    labelAddon,
    validationHint,
    alignLabel,
    reformatLabel,
    labelProps: overrideLabelProps,
    rowData,
    clearable,
  } = props;

  const labelProps = {
    flexBox,
    id,
    includeLabel,
    hint,
    label,
    mandatory,
    readOnly,
    labelAddon,
    validationHint,
    alignLabel,
    reformatLabel,
    ...overrideLabelProps,
  };

  const [hyperlinkClicked, setHyperlinkClicked] = useState(false);
  const nodeRef = useRef<any>();
  const isFocusedRef = useRef(false);
  const precision = usePrecision(props?.valueType);

  const formattedValue = useMemo(() => {
    if (
      (decimals !== null && decimals !== undefined) ||
      type === BlurTextFieldType.Currency ||
      type === BlurTextFieldType.BaseCurrency
    ) {
      let dp: number | undefined;

      if (typeof decimals === 'function' && rowData) {
        dp = (decimals as (rowData: any) => number)(rowData);
      } else if (!isNaN(decimals as any)) {
        //decimals is expected as number || undefined, but old non-ts code would have no validation to prevent other values, so checking for that here.
        dp = decimals;
      } else if (props.valueType) {
        dp = precision;
      }

      if (value === null || isNaN(value as number) || isNaN(Number.parseFloat(value as string))) {
        return value;
      }

      const numberValue = Number.parseFloat(value as string);

      return props.thousandsSeparator
        ? formatValue(value as string, {
            dependentValues: { options: { format: { thousandSeparator: true } } },
            decimals: dp ?? 2,
          })
        : numberValue.toFixed(dp ?? 2);
    }

    return value;
    //Due to generic/old uses, need to be wary of adding other dependencies here - Functions passed likely change every render.
    // (But we wouldn't expect their content to change anyway, should be safe only considering value).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const [textValue, setTextValue] = useState(formattedValue);

  const onChange = (event) => {
    const newValue = getValue(event) ?? null;

    // Moved decimal check here to prevent user from entering excessive decimals, similar behaviour to a standard number input.
    // Resolves temporary save issues with invalid entries + better user experience.
    if (decimals !== null && decimals !== undefined && Number(newValue)) {
      if (
        newValue &&
        newValue.indexOf('.') !== -1 &&
        (decimals === 0 || newValue.length - newValue.indexOf('.') - 1 > decimals)
      ) {
        return;
      }
    }
    if (props.inGrid) {
      props.onChange?.({ target: { value: newValue } });
    } else if (props.enableOnChange) {
      props.onChange?.(newValue);
    }
    setTextValue(newValue);
  };

  useEffect(() => {
    //If focused & the field is numeric - Need to force a proper numeric check.
    //Otherwise, for example, a '10' in the field may mismatch an incoming '10.00' and reset the text highlight unexpectedly.
    if (
      isFocusedRef.current &&
      (decimals || type === BlurTextFieldType.Currency || type === BlurTextFieldType.BaseCurrency)
    ) {
      if (parseFloat(formattedValue as string) !== parseFloat(textValue as string)) {
        setTextValue(formattedValue);
      }
    } else if (formattedValue !== textValue) {
      setTextValue(formattedValue);
    }
  }, [formattedValue]);

  const handleClearInput = (event) => {
    props.onChange?.(props.id, '');
    nodeRef.current?.focus();
  };

  const onClick = (type, event) => {
    if (props.hyperlink) {
      hyperlinkClick(event);
    } else {
      if (type === BlurTextFieldType.Number || (props.decimals !== null && props.decimals !== undefined)) {
        event.target.setSelectionRange(0, event.target.value.length);
      }

      if (props.onClick) {
        props.onClick(event);
      }
    }
  };

  const hyperlinkClick: (event: any, isKeyPress?: boolean) => void = (event, isKeyPress) => {
    if ((isKeyPress && event.shiftKey) || event.ctrlKey) {
      const { value } = props;
      switch (props.hyperlink) {
        case HrefLink.MailTo:
          const re =
            /^(([^<>()\[\]\.,;:\s@\"]+(\.[^<>()\[\]\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()[\]\.,;:\s@\"]+\.)+[^<>()[\]\.,;:\s@\"]{2,})$/i;
          if (re.test(String(value).toLowerCase())) {
            window.location.href = `${HrefLink.MailTo}:${value}`;
            setHyperlinkClicked(true);
          }
          break;
        case HrefLink.Tel:
          window.location.href = `${HrefLink.Tel}:${value}`;
          setHyperlinkClicked(true);
          break;
        case HrefLink.CallTo:
          window.location.href = `${HrefLink.CallTo}:${value}`;
          setHyperlinkClicked(true);
          break;
        default:
          window.open((value as string).includes('http') ? (value as string) : `http://${value}`, '_blank');
          setHyperlinkClicked(true);
      }
    }
    if (props.onClick) props.onClick(event);
  };

  const disabled = props.readOnly ? true : null;
  const { TextComponent, style = {} } = getTextComponent(type);

  let inputType = type || BlurTextFieldType.Text;

  if (type) {
    if (type === BlurTextFieldType.Hide && !props.active) {
      inputType = BlurTextFieldType.Hide;
    }

    // treat as text as 'number' does not support selection
    if (type === BlurTextFieldType.Number) {
      inputType = BlurTextFieldType.Text;
    }
  }

  const controlStyle = hyperlink
    ? {
        ...props.style,
        ...style,
        ...(props.styles?.input || {}),
        color: hyperlinkClicked ? p4Tokens.colorBrandForegroundLinkPressed : p4Tokens.colorBrandForegroundLink,
      }
    : { ...props.style, ...style, ...(props.styles?.input || {}) };

  let control = (
    <TextComponent
      disabled={disabled}
      ref={(element) => {
        nodeRef.current = element;
        if (ref && Object.keys(ref).includes('current')) {
          ref.current = element;
        } else if (ref) {
          ref(element);
        }
      }}
      {...props}
      value={textValue}
      type={inputType}
      onClick={(event) => onClick(type, event)}
      style={{
        ...controlStyle,
      }}
      onKeyPress={(target) => {
        if (props.onKeyPress) props.onKeyPress(target);
        if (target.charCode === 13) hyperlinkClick(target, true);
      }}
      spellCheck={spellCheck}
      onChange={onChange}
      onFocus={(e) => {
        isFocusedRef.current = true;
        props.onFocus?.(e);
      }}
      onBlur={(e) => {
        isFocusedRef.current = false;
        props.onBlur?.(e);
      }}
      clearable={clearable}
    />
  );
  const tooltip = props.readOnly ? props.readOnlyMessage : null;
  if (!(hint || labelProps.hint) && tooltip) labelProps.hint = tooltip;
  if (tooltip || hyperlink) {
    let tooltipText: string;
    if (tooltip) {
      tooltipText = tooltip;
    } else if (hyperlink === 'email') {
      tooltipText = 'Ctrl+click or shift+enter to send email';
    } else {
      tooltipText =
        hyperlink === 'callto' ? 'Ctrl+click or shift+enter to call' : 'Ctrl+click or shift+enter to open hyperlink';
    }

    control = (
      <TriggerPopover flexBox={false} popover={<DeprecatedTooltip value={tooltipText} />} styles={'width: 100%'}>
        <div>{control}</div>
      </TriggerPopover>
    );
  }

  if (props.inGrid) {
    if (clearButton) {
      return (
        <div>
          {control}
          <ClearInput value={value} clearButton={clearButton} onMouseDown={handleClearInput} marginTop={'0.3rem'} />
        </div>
      );
    }
    return control;
  }
  return (
    <InputBlock flexBox={flexBox}>
      <InputLabel {...labelProps} cols={labelCols} />
      <InputGroup
        flexBox={flexBox}
        cols={includeLabel ? inputCols : 1}
        withLabel={includeLabel}
        style={{ display: clearButton ? 'flex' : undefined }}
      >
        <InputPrefix inputPrefix={inputPrefix} />
        {control}
        <InputSuffix inputSuffix={inputSuffix} />
        <ClearInput value={value} clearButton={clearButton} onMouseDown={handleClearInput} />
        {props.copy ? <CopyInput value={value} /> : null}
        <InputErrorMessage
          label={label}
          active={active}
          touched={touched}
          validationErrors={validationErrors}
          showWhenNotActive={showWhenNotActive}
          customValidationErrors={customValidationErrors}
        />
        <InputInfoMessage message={infoMessage} />
      </InputGroup>
    </InputBlock>
  );
});

(BlurTextField as any).defaultProps = {
  active: false,
  mandatory: false,
  clearButton: false,
  placeholder: '',
  onChange: () => {},
  includeLabel: true,
  readOnly: false,
  flexBox: true,
  labelCols: 1 / 4,
  inputCols: 3 / 4,
  value: '',
  validationErrors: [],
  hint: '',
  showWhenNotActive: false,
  type: BlurTextFieldType.Text,
  label: '',
  id: '',
  spellCheck: 'true',
  enableOnChange: false,
};

export default BlurTextField;
